import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Youtube } from "lucide-react";
import { TiktokIcon } from "./icons/TiktokIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.name.length < 2) {
      toast.error("Podaj swoje imię i nazwisko");
      return;
    }
    if (!form.email.includes("@")) {
      toast.error("Podaj poprawny adres e-mail");
      return;
    }
    if (!form.message || form.message.length < 5) {
      toast.error("Napisz kilka słów o sytuacji");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        phone: form.phone || null,
        company: form.company || null,
        source: "landing-contact",
      });
      setSent(true);
      toast.success("Wiadomość wysłana! Odezwę się w ciągu 24h.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error("Nie udało się wysłać. Spróbuj ponownie lub napisz bezpośrednio na e-mail.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="section relative">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <span className="eyebrow">Bezpłatna rozmowa</span>
            <h2 className="h-display mt-4 text-[clamp(28px,3.6vw,44px)]">
              Masz firmę transportową i czujesz, że{" "}
              <span className="gold-gradient-text italic">coś nie działa?</span>
            </h2>
            <div className="gold-line mt-6" />
            <p className="mt-8 text-[16px] leading-relaxed text-[#b8b3a7]">
              Nie musisz tego rozwiązywać sam. Napisz mi kilka zdań o sytuacji,
              a odezwę się w ciągu 24 godzin i umówimy bezpłatną, krótką
              rozmowę. Bez zobowiązań.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href="mailto:kontakt@przemyslawchudy.pl"
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <div className="h-12 w-12 border border-[rgba(212,169,74,0.3)] flex items-center justify-center group-hover:border-[var(--gold)] transition-colors">
                  <Mail className="text-[var(--gold)]" size={18} />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.26em] uppercase text-[#b8b3a7]">
                    E-mail
                  </div>
                  <div className="text-[16px] text-[#f5f1e8] group-hover:text-[var(--gold)] transition-colors">
                    kontakt@przemyslawchudy.pl
                  </div>
                </div>
              </a>

              <a
                href="tel:+48660341213"
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <div className="h-12 w-12 border border-[rgba(212,169,74,0.3)] flex items-center justify-center group-hover:border-[var(--gold)] transition-colors">
                  <Phone className="text-[var(--gold)]" size={18} />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.26em] uppercase text-[#b8b3a7]">
                    Telefon
                  </div>
                  <div className="text-[16px] text-[#f5f1e8] group-hover:text-[var(--gold)] transition-colors">
                    +48 660 341 213
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <a
                    href="https://www.tiktok.com/@chudyprzemyslaw"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-tiktok"
                    aria-label="TikTok"
                    className="h-12 w-12 border border-[rgba(212,169,74,0.3)] flex items-center justify-center text-[var(--gold)] hover:border-[var(--gold)] hover:bg-[rgba(212,169,74,0.06)] transition-all"
                  >
                    <TiktokIcon size={18} />
                  </a>
                  <a
                    href="https://www.youtube.com/@chudyprzemyslaw"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-youtube"
                    aria-label="YouTube"
                    className="h-12 w-12 border border-[rgba(212,169,74,0.3)] flex items-center justify-center text-[var(--gold)] hover:border-[var(--gold)] hover:bg-[rgba(212,169,74,0.06)] transition-all"
                  >
                    <Youtube size={18} />
                  </a>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.26em] uppercase text-[#b8b3a7]">
                    Social media
                  </div>
                  <div className="text-[14px] text-[#f5f1e8]">
                    TikTok · YouTube <span className="gold-text">@chudyprzemyslaw</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="h-12 w-12 border border-[rgba(212,169,74,0.3)] flex items-center justify-center">
                  <MapPin className="text-[var(--gold)]" size={18} />
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.26em] uppercase text-[#b8b3a7]">
                    Obszar działania
                  </div>
                  <div className="text-[16px] text-[#f5f1e8]">
                    Cała Polska • online & stacjonarnie
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <div
                data-testid="contact-success"
                className="card-dark p-12 text-center"
              >
                <div className="h-16 w-16 border border-[var(--gold)] rounded-full flex items-center justify-center mx-auto">
                  <Send className="text-[var(--gold)]" size={24} />
                </div>
                <h3 className="h-display text-[28px] md:text-[34px] mt-6 text-[#f5f1e8]">
                  Dziękuję za wiadomość
                </h3>
                <p className="mt-4 text-[16px] text-[#b8b3a7] max-w-md mx-auto">
                  Odezwę się w ciągu 24 godzin. W międzyczasie sprawdź sekcję
                  e-booków — może znajdziesz tam coś pomocnego na już.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="btn-ghost mt-8"
                  data-testid="contact-new-message"
                >
                  Wyślij kolejną wiadomość
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="card-dark p-8 lg:p-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="lbl" htmlFor="c-name">Imię i nazwisko *</label>
                    <input
                      id="c-name"
                      data-testid="contact-input-name"
                      className="field"
                      type="text"
                      required
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jan Kowalski"
                    />
                  </div>
                  <div>
                    <label className="lbl" htmlFor="c-email">E-mail *</label>
                    <input
                      id="c-email"
                      data-testid="contact-input-email"
                      className="field"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="jan@firma.pl"
                    />
                  </div>
                  <div>
                    <label className="lbl" htmlFor="c-phone">Telefon</label>
                    <input
                      id="c-phone"
                      data-testid="contact-input-phone"
                      className="field"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+48 ..."
                    />
                  </div>
                  <div>
                    <label className="lbl" htmlFor="c-company">Firma</label>
                    <input
                      id="c-company"
                      data-testid="contact-input-company"
                      className="field"
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Nazwa firmy"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="lbl" htmlFor="c-message">Napisz kilka zdań o sytuacji *</label>
                    <textarea
                      id="c-message"
                      data-testid="contact-input-message"
                      className="field min-h-[160px] resize-y"
                      required
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Opisz krótko: gdzie jesteś jako firma, co próbowałeś i czego potrzebujesz."
                    />
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
                  <p className="text-[11px] text-[#7a766d] tracking-wide">
                    * pola wymagane. Odpisuję w ciągu 24h.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="btn-gold disabled:opacity-60"
                  >
                    {loading ? "Wysyłam..." : "Wyślij wiadomość"}
                    <Send size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
